import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../../lib/api'

interface AnalyticsData {
  overview: {
    totalUsers: number
    totalPosts: number
    totalMessages: number
    dailyActiveUsers: number
    weeklyActiveUsers: number
    monthlyActiveUsers: number
  }
  growth: {
    usersThisWeek: number
    usersLastWeek: number
    postsThisWeek: number
    postsLastWeek: number
  }
  engagement: {
    avgPostsPerUser: number
    avgLikesPerPost: number
    avgCommentsPerPost: number
    avgSessionDuration: number
  }
  demographics: {
    ageGroups: { label: string; value: number }[]
  }
  moderation: {
    contentBlockedToday: number
    contentFlaggedToday: number
    reportsToday: number
    avgResponseTime: number
  }
  topContent: {
    posts: { id: string; content: string; likes: number; author: string }[]
    hashtags: { tag: string; count: number }[]
  }
}

export default function AnalyticsPage() {
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | '90d'>('7d')

  useEffect(() => {
    fetchAnalytics()
  }, [timeRange])

  const fetchAnalytics = async () => {
    setLoading(true)
    try {
      const response = await api.get('/admin/analytics', {
        params: { range: timeRange },
      })
      setData(response.data)
    } catch (error) {
      console.error('Failed to fetch analytics:', error)
      // Mock data
      setData({
        overview: {
          totalUsers: 15420,
          totalPosts: 89234,
          totalMessages: 234567,
          dailyActiveUsers: 2341,
          weeklyActiveUsers: 8923,
          monthlyActiveUsers: 12456,
        },
        growth: {
          usersThisWeek: 892,
          usersLastWeek: 756,
          postsThisWeek: 4523,
          postsLastWeek: 3890,
        },
        engagement: {
          avgPostsPerUser: 5.8,
          avgLikesPerPost: 23.4,
          avgCommentsPerPost: 4.2,
          avgSessionDuration: 18.5,
        },
        demographics: {
          ageGroups: [
            { label: '12-13 (Child)', value: 2345 },
            { label: '14-15 (Child)', value: 3456 },
            { label: '16-17 (Teen)', value: 4567 },
            { label: '18-21 (Adult)', value: 5052 },
          ],
        },
        moderation: {
          contentBlockedToday: 23,
          contentFlaggedToday: 45,
          reportsToday: 12,
          avgResponseTime: 2.3,
        },
        topContent: {
          posts: [
            { id: '1', content: 'Check out my new art! 🎨', likes: 1234, author: '@creative_artist' },
            { id: '2', content: 'Just hit 1000 followers! Thank you all 💕', likes: 987, author: '@rising_star' },
            { id: '3', content: 'New music video dropping soon 🔥', likes: 876, author: '@music_producer' },
          ],
          hashtags: [
            { tag: '#art', count: 4523 },
            { tag: '#music', count: 3890 },
            { tag: '#photography', count: 2345 },
            { tag: '#klassic', count: 2100 },
            { tag: '#creative', count: 1890 },
          ],
        },
      })
    } finally {
      setLoading(false)
    }
  }

  const calculateGrowth = (current: number, previous: number) => {
    if (previous === 0) return 0
    return ((current - previous) / previous * 100).toFixed(1)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Link to="/admin" className="text-gray-400 hover:text-white">
                Admin
              </Link>
              <span className="text-gray-600">/</span>
              <h1 className="text-xl font-bold">Analytics</h1>
            </div>
            <p className="text-gray-400 text-sm">Platform metrics and insights</p>
          </div>
          <div className="flex gap-2">
            {(['24h', '7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg transition ${
                  timeRange === range
                    ? 'bg-pink-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="p-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Users"
            value={data?.overview.totalUsers.toLocaleString() || '0'}
            change={`+${calculateGrowth(data?.growth.usersThisWeek || 0, data?.growth.usersLastWeek || 0)}%`}
            positive
          />
          <MetricCard
            title="Daily Active Users"
            value={data?.overview.dailyActiveUsers.toLocaleString() || '0'}
            subtitle={`${((data?.overview.dailyActiveUsers || 0) / (data?.overview.totalUsers || 1) * 100).toFixed(1)}% of total`}
          />
          <MetricCard
            title="Posts This Week"
            value={data?.growth.postsThisWeek.toLocaleString() || '0'}
            change={`+${calculateGrowth(data?.growth.postsThisWeek || 0, data?.growth.postsLastWeek || 0)}%`}
            positive
          />
          <MetricCard
            title="Avg Session Duration"
            value={`${data?.engagement.avgSessionDuration || 0} min`}
            subtitle="Time spent per session"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Engagement Metrics */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-lg font-semibold mb-4">Engagement</h2>
            <div className="space-y-4">
              <EngagementRow
                label="Avg Posts/User"
                value={data?.engagement.avgPostsPerUser || 0}
                max={10}
              />
              <EngagementRow
                label="Avg Likes/Post"
                value={data?.engagement.avgLikesPerPost || 0}
                max={50}
              />
              <EngagementRow
                label="Avg Comments/Post"
                value={data?.engagement.avgCommentsPerPost || 0}
                max={10}
              />
            </div>
          </div>

          {/* Age Demographics */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-lg font-semibold mb-4">Age Demographics</h2>
            <div className="space-y-3">
              {data?.demographics.ageGroups.map((group) => (
                <div key={group.label} className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">{group.label}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-pink-500 h-2 rounded-full"
                        style={{
                          width: `${(group.value / (data?.overview.totalUsers || 1)) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm w-16 text-right">{group.value.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Safety Metrics */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-lg font-semibold mb-4">Safety Metrics</h2>
            <div className="grid grid-cols-2 gap-4">
              <SafetyMetric
                label="Content Blocked"
                value={data?.moderation.contentBlockedToday || 0}
                icon="🛡️"
              />
              <SafetyMetric
                label="Content Flagged"
                value={data?.moderation.contentFlaggedToday || 0}
                icon="🚩"
              />
              <SafetyMetric
                label="Reports Today"
                value={data?.moderation.reportsToday || 0}
                icon="📋"
              />
              <SafetyMetric
                label="Avg Response"
                value={`${data?.moderation.avgResponseTime || 0}h`}
                icon="⏱️"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Posts */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-lg font-semibold mb-4">Top Posts</h2>
            <div className="space-y-4">
              {data?.topContent.posts.map((post, index) => (
                <div key={post.id} className="flex items-center gap-4">
                  <span className="text-2xl font-bold text-gray-600">#{index + 1}</span>
                  <div className="flex-1">
                    <p className="text-sm line-clamp-1">{post.content}</p>
                    <p className="text-xs text-gray-500">{post.author}</p>
                  </div>
                  <span className="text-pink-400">❤️ {post.likes}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trending Hashtags */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-lg font-semibold mb-4">Trending Hashtags</h2>
            <div className="flex flex-wrap gap-3">
              {data?.topContent.hashtags.map((hashtag) => (
                <span
                  key={hashtag.tag}
                  className="bg-gray-700 px-4 py-2 rounded-full text-sm hover:bg-gray-600 cursor-pointer transition"
                >
                  {hashtag.tag}
                  <span className="ml-2 text-gray-400">{hashtag.count.toLocaleString()}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface MetricCardProps {
  title: string
  value: string
  change?: string
  subtitle?: string
  positive?: boolean
}

function MetricCard({ title, value, change, subtitle, positive }: MetricCardProps) {
  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <p className="text-gray-400 text-sm mb-2">{title}</p>
      <p className="text-3xl font-bold mb-1">{value}</p>
      {change && (
        <p className={positive ? 'text-green-400 text-sm' : 'text-red-400 text-sm'}>
          {change} vs last period
        </p>
      )}
      {subtitle && <p className="text-gray-500 text-sm">{subtitle}</p>}
    </div>
  )
}

interface EngagementRowProps {
  label: string
  value: number
  max: number
}

function EngagementRow({ label, value, max }: EngagementRowProps) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-400">{label}</span>
        <span>{value.toFixed(1)}</span>
      </div>
      <div className="bg-gray-700 rounded-full h-2">
        <div
          className="bg-gradient-to-r from-pink-500 to-purple-500 h-2 rounded-full"
          style={{ width: `${Math.min((value / max) * 100, 100)}%` }}
        />
      </div>
    </div>
  )
}

interface SafetyMetricProps {
  label: string
  value: number | string
  icon: string
}

function SafetyMetric({ label, value, icon }: SafetyMetricProps) {
  return (
    <div className="bg-gray-900 rounded-lg p-3 text-center">
      <span className="text-2xl">{icon}</span>
      <p className="text-xl font-bold mt-1">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  )
}
